/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useId, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { Separator } from '@/components/ui/separator';
import { ChevronDownIcon, ChevronRightIcon, PriceShoesMarkIcon } from '@/components/icons';
import { cn } from '@/lib/utils';
import type { NavColumn, NavItem, NavLinkItem, NavService } from '@/lib/header-menu/types';
import MenuHref from './menu-href';

// Same visual treatment as the category mega-menu's top-level triggers.
const TOP_LEVEL_CLASS = cn(
    navigationMenuTriggerStyle(),
    'h-8 px-1.5 py-0 text-base font-medium leading-6 text-header-menu-foreground [--ui-radius:var(--radius-lg)]'
);
// Card anchored under its trigger (Figma: Menu dsplegable Priceshoes / Dropdown Servicios): 3px accent
// top border and rounded bottom corners. `!` beats the primitive's `viewport=false` variant classes.
const PANEL_CONTENT_CLASS = cn(
    '!mt-3 z-50 flex flex-col gap-4 !p-4 !animate-none !transition-none',
    '!bg-header-menu-background text-header-menu-foreground',
    '!border-0 !border-t-[3px] !border-header-menu-panel-border',
    '[--ui-radius:0_0_var(--radius-2xl)_var(--radius-2xl)] [--ui-shadow:0_2px_23px_rgb(0_0_0/0.07)]'
);
const PANEL_HOVER_CLASS = 'hover:!bg-header-menu-item-hover-background focus:!bg-header-menu-item-hover-background';
const PANEL_ITEM_CLASS = cn(
    'w-full flex-row items-center px-4 py-1.5 text-sm leading-[22px] [--ui-radius:var(--radius-lg)]',
    'hover:!text-header-menu-accent focus:!text-header-menu-accent',
    PANEL_HOVER_CLASS
);
const PANEL_FOOTER_LINK_CLASS = cn(
    'h-9 flex-row items-center self-start whitespace-nowrap px-2 py-1.5 text-base leading-[22px] font-semibold !text-header-menu-accent [--ui-radius:var(--radius-lg)]',
    PANEL_HOVER_CLASS
);

function PanelItem({
    link,
    className,
    ariaLabel,
}: {
    link: NavLinkItem;
    className?: string;
    ariaLabel?: string;
}): ReactElement {
    return (
        <NavigationMenuLink asChild className={cn(PANEL_ITEM_CLASS, className)}>
            <MenuHref link={link} aria-label={ariaLabel}>
                <span className="min-w-0 flex-1 truncate">{link.label}</span>
            </MenuHref>
        </NavigationMenuLink>
    );
}

// Divider plus "Ver todas las categorías ›" / "Ver todos los servicios ›" to the dropdown's landing page.
function PanelFooter({ landing, label }: { landing?: NavLinkItem; label: string }): ReactElement | null {
    if (!landing) return null;
    return (
        <div className="flex w-full flex-col gap-2">
            <Separator className="bg-header-menu-panel-divider" />
            <NavigationMenuLink asChild className={PANEL_FOOTER_LINK_CLASS}>
                <MenuHref link={landing}>
                    {label}
                    <ChevronRightIcon className="size-5 shrink-0 !text-current" />
                </MenuHref>
            </NavigationMenuLink>
        </div>
    );
}

// A column: its heading, its links (already capped on the server) and "Ver todas" when it has a target.
function MenuColumn({ column }: { column: NavColumn }): ReactElement {
    const { t } = useTranslation('header');
    const headingId = useId();
    return (
        <li className="flex w-56 flex-col">
            <p
                id={headingId}
                className="truncate px-4 py-1.5 text-sm leading-[22px] font-semibold uppercase text-header-menu-heading">
                {column.title}
            </p>
            <ul aria-labelledby={headingId} className="flex flex-col">
                {column.links.map((link) => (
                    <li key={`${link.label}|${link.href}`}>
                        <PanelItem link={link} className="text-header-menu-item" />
                    </li>
                ))}
                {column.viewAll ? (
                    <li>
                        <PanelItem
                            link={{ ...column.viewAll, label: t('megaMenu.viewAll') }}
                            ariaLabel={t('megaMenu.viewAllIn', { category: column.title })}
                            className="font-bold !text-header-menu-accent"
                        />
                    </li>
                ) : null}
            </ul>
        </li>
    );
}

function ColumnsPanel({ columns, landing }: { columns: NavColumn[]; landing?: NavLinkItem }): ReactElement {
    const { t } = useTranslation('header');
    return (
        <>
            <ul className="flex items-start">
                {columns.map((column) => (
                    <MenuColumn key={column.title} column={column} />
                ))}
            </ul>
            <PanelFooter landing={landing} label={t('megaMenu.viewAllCategories')} />
        </>
    );
}

// Merchant icon from Page Designer, or the brand mark on the accent badge when there is none.
function ServiceIcon({ icon }: { icon?: string }): ReactElement {
    if (icon) {
        return <img src={icon} alt="" width={75} height={32} className="h-8 w-[75px] shrink-0 object-contain" />;
    }
    return (
        <span className="flex h-8 w-[75px] shrink-0 items-center justify-center rounded-lg bg-header-menu-service-badge text-header-menu-service-badge-foreground">
            {/* `!`: the unlayered `header svg` rule (theme/overrides/navigation.css) beats plain utilities. */}
            <PriceShoesMarkIcon className="size-4 !text-current" />
        </span>
    );
}

function ServicesPanel({ services, landing }: { services: NavService[]; landing?: NavLinkItem }): ReactElement {
    const { t } = useTranslation('header');
    return (
        <>
            <ul className="flex flex-col gap-4">
                {services.map((link) => (
                    <li key={`${link.label}|${link.href}`}>
                        <NavigationMenuLink
                            asChild
                            className={cn(
                                'w-[400px] flex-row items-start gap-4 p-3 [--ui-radius:var(--radius-2xl)]',
                                PANEL_HOVER_CLASS
                            )}>
                            <MenuHref link={link}>
                                <ServiceIcon icon={link.icon} />
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                    <span className="text-base leading-5 font-semibold text-header-menu-service-title">
                                        {link.label}
                                    </span>
                                    {link.description ? (
                                        <span className="text-xs leading-4 font-normal text-header-menu-service-description">
                                            {link.description}
                                        </span>
                                    ) : null}
                                </span>
                            </MenuHref>
                        </NavigationMenuLink>
                    </li>
                ))}
            </ul>
            <PanelFooter landing={landing} label={t('megaMenu.viewAllServices')} />
        </>
    );
}

/**
 * Desktop header menu authored in Page Designer (header `navigation` region, see `@/lib/header-menu`).
 * Link items navigate; dropdown items are disclosure triggers only (WCAG 3.2.2), like the
 * category mega-menu, and their panels hold the navigable links (including the landing page).
 */
export default function MainNavigationBar({ items }: { items: NavItem[] }): ReactElement {
    return (
        <NavigationMenu viewport={false} delayDuration={0}>
            <NavigationMenuList className="gap-6">
                {items.map((item) => (
                    <NavigationMenuItem key={`${item.type}|${item.label}`}>
                        {item.type === 'link' ? (
                            <NavigationMenuLink asChild className={TOP_LEVEL_CLASS}>
                                <MenuHref link={item}>{item.label}</MenuHref>
                            </NavigationMenuLink>
                        ) : (
                            <>
                                <NavigationMenuTrigger className={TOP_LEVEL_CLASS}>
                                    {item.label}
                                    {/* Same chevron as the category mega-menu triggers (navigation-menu/impl.tsx). */}
                                    <ChevronDownIcon className="ml-1 size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                </NavigationMenuTrigger>
                                <NavigationMenuContent className={PANEL_CONTENT_CLASS}>
                                    {item.type === 'columns' ? (
                                        <ColumnsPanel columns={item.columns} landing={item.landing} />
                                    ) : (
                                        <ServicesPanel services={item.services} landing={item.landing} />
                                    )}
                                </NavigationMenuContent>
                            </>
                        )}
                    </NavigationMenuItem>
                ))}
            </NavigationMenuList>
        </NavigationMenu>
    );
}
